export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Thin fetch wrapper for same-origin JSON endpoints. Network failures surface as `TypeError`. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  // FormData bodies set their own multipart boundary.
  if (typeof init.body === 'string') headers.set('Content-Type', 'application/json');

  const res = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
  return (await res.json()) as T;
}

/**
 * No connection, no answer in time, or the server is down behind the proxy: all "offline".
 */
export const isNetworkError = (err: unknown): boolean =>
  err instanceof TypeError ||
  (err instanceof DOMException && err.name === 'TimeoutError') ||
  (err instanceof ApiError && [502, 503, 504].includes(err.status));
